import './VotesDetail.css';
import React from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import takesData from './takesData.json';
import PerspectiveInfoSheet from './PerspectiveInfoSheet.jsx';
import { PERSP_COLOR } from './PerspChart.jsx';

function getAgreePct(votes) {
    const total = votes.number_agree + votes.number_disagree;
    if (total === 0) return 0;
    return Math.round((votes.number_agree / total) * 100);
}
function getTotalVotes(votes) {
    return votes.number_agree + votes.number_disagree;
}

const VdRow = ({ label, votes, delay = 0, color, countStr }) => {
    const agree    = getAgreePct(votes);
    const disagree = 100 - agree;
    const total    = getTotalVotes(votes);

    // Trigger clip-path reveal after mount so CSS transition has a "from" state
    const [revealed, setRevealed] = React.useState(false);
    React.useEffect(() => {
        const raf = requestAnimationFrame(() =>
            requestAnimationFrame(() => setRevealed(true))
        );
        return () => cancelAnimationFrame(raf);
    }, []);

    return (
        <div className="vd-row-block" style={{ animationDelay: `${delay}ms` }}>
            <div className="vd-row-header">
                <span className="vd-row-name">{label}</span>
                <span className="vd-row-count">{countStr ?? `${total} votes`}</span>
            </div>
            <div className="vd-row-bar">
                <div
                    className="vd-row-bar-a"
                    style={{
                        flex: agree,
                        background: color || undefined,
                        clipPath: revealed ? 'inset(0 0% 0 0 round 5px)' : 'inset(0 100% 0 0 round 5px)',
                        transitionDelay: `${delay}ms`,
                    }}
                >
                    <span className="vd-bar-pct" style={{ color: color ? '#0C1E2E' : undefined }}>{agree}%</span>
                </div>
                <div
                    className="vd-row-bar-d"
                    style={{
                        flex: disagree,
                        clipPath: revealed ? 'inset(0 0 0 0% round 5px)' : 'inset(0 0 0 100% round 5px)',
                        transitionDelay: `${delay}ms`,
                    }}
                >
                    <span className="vd-bar-pct">{disagree}%</span>
                </div>
            </div>
        </div>
    );
};


const VotesDetail = () => {
    const { id }   = useParams();
    const history  = useHistory();
    const { t, i18n } = useTranslation();

    // Lens index: 0 = Perspectives, 1 = Region, 2 = Age (matches groupVotes order)
    const [lens, setLens] = React.useState(0);
    const [sheetOpen, setSheetOpen] = React.useState(false);

    const take = takesData.data.find((t) => String(t.id) === String(id));
    if (!take || !take.groupVotes) return null;

    const lang = i18n.language?.slice(0, 2) || 'en';

    const getText = () => {
        if (lang === 'de') return take.text_de || take.headline_de;
        if (lang === 'fr') return take.text_fr || take.headline_fr;
        return take.text_en || take.headline_en;
    };

    const getSource = () => {
        if (lang === 'de') return take.source_de || take.date_de;
        if (lang === 'fr') return take.source_fr || take.date_fr;
        return take.source_en || take.date_en;
    };

    const getUsername = () => {
        if (!take.user) return '';
        if (lang === 'de' && take.user.username_de) return take.user.username_de;
        if (lang === 'fr' && take.user.username_fr) return take.user.username_fr;
        return take.user.username || '';
    };

    const getUserTitle = () => {
        if (!take.user) return '';
        if (lang === 'de') return take.user.userTitle_de;
        if (lang === 'fr') return take.user.userTitle_fr;
        return take.user.userTitle_en;
    };

    const getTakeaway = () => {
        if (lang === 'de') return take.takeaway_de;
        if (lang === 'fr') return take.takeaway_fr;
        return take.takeaway_en;
    };

    const groupLabel = (group) => {
        if (lang === 'de') return group.name_de;
        if (lang === 'fr') return group.name_fr;
        return group.name_en;
    };

    const isInsight = take.type === 'insight' || !take.user;
    const activeGroup = take.groupVotes[lens] ?? take.groupVotes[0];
    const takeaway = getTakeaway();

    return (
        <div className="vd vd-page">
            <div className="vd-body">
                <button className="vd-back" onClick={() => history.goBack()}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10 3L5 8l5 5" />
                    </svg>
                    {t('vd.back')}
                </button>
                <div className="vd-orig">
                    <p className="vd-quote">
                        {!isInsight && <span className="vd-q">&ldquo;</span>}
                        {getText()}
                        {!isInsight && <span className="vd-q">&rdquo;</span>}
                    </p>
                    {isInsight ? (
                        <div className="vd-source">{getSource()}</div>
                    ) : (
                        <div className="vd-speaker">
                            {take.user.userImg ? (
                                <img className="vd-speaker-img" src={take.user.userImg} alt={getUsername()} />
                            ) : (
                                <div className="vd-speaker-initials">
                                    {take.user.initials || getUsername().slice(0, 2).toUpperCase()}
                                </div>
                            )}
                            <div>
                                <div className="vd-speaker-name">{getUsername()}</div>
                                <div className="vd-speaker-role">{getUserTitle()}</div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Lens switcher - Perspectives / Region / Age */}
                <div className="vd-lens-bar">
                    <div className="takes-seg-control">
                        {take.groupVotes.map((group, gi) => (
                            <button
                                key={gi}
                                className={`takes-seg-btn vd-seg-btn${lens === gi ? ' active' : ''}`}
                                onClick={() => setLens(gi)}
                            >
                                {groupLabel(group)}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Info link - only visible on Perspectives tab */}
                {lens === 0 && (
                    <button className="vd-persp-info-link" onClick={() => setSheetOpen(true)}>
                        {t('vd.perspLink')} →
                    </button>
                )}

                {/* Takeaway insight line - only on Perspectives tab */}
                {lens === 0 && takeaway && (
                    <p className="vd-takeaway">{takeaway}</p>
                )}

                {/* Bar segment legend */}
                <div className="vd-bar-legend">
                    <span>{t('vd.agree')}</span>
                    <span>{t('vd.disagree')}</span>
                </div>

                {/* Active lens - flat bar rows */}
                <div className="vd-rows-lens">
                    {activeGroup.groups.map((sg, sgi) => {
                        const total = getTotalVotes(sg.votes);
                        const countStr = `${total.toLocaleString(lang)} ${t('vd.votes')}`;
                        return (
                            <VdRow
                                key={`${lens}-${sgi}`}
                                label={groupLabel(sg)}
                                votes={sg.votes}
                                delay={sgi * 80}
                                color={PERSP_COLOR[sg.id]}
                                countStr={countStr}
                            />
                        );
                    })}
                </div>

                <div className="vd-copyright">Prototype by Ferdinand Riedl &middot; riedlsoftware.com</div>
            </div>

            <PerspectiveInfoSheet open={sheetOpen} onClose={() => setSheetOpen(false)} />
        </div>
    );
};

export default VotesDetail;
